import {
  Component,
  Input,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { TripistEmbedService } from './tripist-embed.service';
import { SessionService } from '../../session.service';
import { IntegrationsService } from '../../integrations/integrations.service';

@Component({
  selector: 'app-tripist-embed',
  templateUrl: './tripist-embed.component.html',
  styleUrls: ['./tripist-embed.component.scss'],
})
export class TripistEmbedComponent implements AfterViewInit, OnDestroy {
  @Input() integrationExternalId!: string;
  @Input() integrationId!: number;
  @Input() autoIdentify = false;
  @Input() forceNewThread = false;
  @Input() isTestThread = false;
  @Input() viewMode: 'mobile' | 'desktop' = 'mobile';
  @ViewChild('tripistEmbedContainer', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  loading = true;
  error: string | null = null;

  private readonly containerId = 'tripist-embed-' + Math.random().toString(36).substring(2, 9);

  constructor(
    private tripistEmbedService: TripistEmbedService,
    private sessionService: SessionService,
    private integrationsService: IntegrationsService,
  ) {}

  ngAfterViewInit(): void {
    this.containerRef.nativeElement.id = this.containerId;

    this.tripistEmbedService
      .loadScript()
      .then(() => {
        const tripist = (window as any).tripist;
        if (!tripist) {
          this.error = 'Tripist script loaded but tripist object not found on window.';
          this.loading = false;
          return;
        }

        if (this.forceNewThread) {
          const prefix = `tripist_${this.integrationExternalId}_`;
          localStorage.removeItem(`${prefix}thread_id`);
          localStorage.removeItem(`${prefix}results`);
        }
        const prefix = `tripist_${this.integrationExternalId}_`;
        const savedThreadId = localStorage.getItem(`${prefix}thread_id`);
        if (!savedThreadId || savedThreadId === 'undefined' || savedThreadId === 'null') {
          localStorage.removeItem(`${prefix}thread_id`);
        }

        const initParams: any = {
          integrationId: this.integrationExternalId,
          elementSelector: `#${this.containerId}`,
          environment: this.tripistEmbedService.tripistEnvironment,
          isTestThread: this.isTestThread === true,
          disableResultsCache: true,
        };

        if (this.autoIdentify && this.integrationId) {
          initParams.onReady = () => this.performAutoIdentify(tripist);
        }

        tripist.init(initParams);

        this.loading = false;
      })
      .catch((err: any) => {
        console.error('Failed to load Tripist script:', err);
        this.error = 'Failed to load the Tripist widget script.';
        this.loading = false;
      });
  }

  private performAutoIdentify(tripist: any): void {
    const user = this.sessionService.currentUserValue;
    const account = this.sessionService.currentAccountValue;
    if (!user || !account) return;

    this.integrationsService.computeIdentityHash(
      account.accountID,
      this.integrationId,
      String(user.id),
    ).pipe(take(1)).subscribe({
      next: (response: any) => {
        if (response.supported === false) return;

        const providerType = response.provider_type;
        if (providerType === 'identity_hmac' || providerType === 'identity_simulator') {
          tripist.identify({
            externalUserId: String(user.id),
            email: user.email,
            firstName: user.first_name,
            lastName: user.last_name,
            hash: response.hash,
          });
        }
      },
      error: (err: any) => {
        console.error('Auto-identify failed:', err);
      },
    });
  }

  ngOnDestroy(): void {
    const tripist = (window as any).tripist;
    if (tripist && typeof tripist.destroy === 'function') {
      tripist.destroy();
    }

    document.querySelectorAll('link[id^="tripist-"], style[id^="tripist-"]').forEach((el) => el.remove());
  }
}
